import SearchIcon from 'assets/ver2/icons/search-icon.svg';
import {
  fetchTransactionsFiltered,
  getBodyPayloadFiltered,
  invalidatePendingFxTransactionsFetches,
} from 'components/FXTransactions/func';
import { REF_NUMBER_LENGTH_MAX } from 'components/Transactions/components/SearchTransaction';
import {
  DEBOUNCE_SEC,
  DESKTOP_MIN_WIDTH,
  ENTER_KEY_DOWN,
  PAGE_DEFAULT,
} from 'constants';
import { FX_TRANSACTION_TABLE_SORT_ORDER_INITIAL } from 'constants/fx';
import { refNumberPhoneValid, searchTextValid } from 'helpers';
import useAuth from 'hooks/useAuth';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const TRANSACTION_SEARCH_INPUT_FIELD = 'transaction-search-input-field';

const normalizeSearchText = (value) =>
  (value ?? '').trim().split(' ').filter(Boolean).join(' ');

const SearchTransaction = ({
  applyFiltered,
  transactions,
  setTransactions,
  searchValue,
  setSearchValue,
  setTotalPages,
  setCurrentPage,
  setSortOrder,
  setTotalElements,
  setFetching = () => {},
}) => {
  const { t } = useLang();
  const { token } = useAuth();

  const [isSearchSubmittedInvalid, setSearchSubmittedInvalid] = useState(false);
  const isSearchInvalid =
    isSearchSubmittedInvalid && !searchTextValid(searchValue);

  // The accepted text as of the latest render, so a queued clear-reload can
  // tell whether the box has text again.
  const searchValueRef = useRef(searchValue);
  searchValueRef.current = searchValue;

  // The valid text last submitted (whitespace-normalised), so a blur does not
  // repeat that search.
  const lastSubmittedRef = useRef(null);

  useEffect(() => {
    if (!searchValue) lastSubmittedRef.current = null;
  }, [searchValue]);

  // True from a clear until its reload runs or is dropped.
  const clearReloadPendingRef = useRef(false);
  // True while a clear shows the spinner only to keep this input mounted (on
  // desktop with no filters, an empty list and empty text hide it unless
  // fetching); unmounting would cancel the queued reload and lose the next
  // keystroke.
  const clearHeldSpinnerRef = useRef(false);
  const releaseClearSpinner = () => {
    if (!clearHeldSpinnerRef.current) return;
    clearHeldSpinnerRef.current = false;
    setFetching(false);
  };
  const reloadUnsearchedList = () => {
    clearReloadPendingRef.current = false;
    clearHeldSpinnerRef.current = false;
    setFetching(true);
    fetchTransactionsFiltered({
      token,
      body: getBodyPayloadFiltered(applyFiltered),
      page: PAGE_DEFAULT,
      setTransactions,
      setCurrentPage,
      setTotalPages,
      setTotalElements,
      setFetching,
    });
  };

  // Reloads DEBOUNCE_SEC after the last clear if the box is then empty, so a
  // transient empty value (Backspace-style IME) neither blanks nor reloads.
  const { debounce } = useDebounceCallback(() => {
    // Already run by an empty submit.
    if (!clearReloadPendingRef.current) return;

    if (searchValueRef.current) {
      clearReloadPendingRef.current = false;
      releaseClearSpinner();
      return;
    }

    reloadUnsearchedList();
  }, DEBOUNCE_SEC);

  const searchByRefNumber = async (value) => {
    if (value?.length <= REF_NUMBER_LENGTH_MAX) {
      console.log('handleSearchTransaction >>> searchByRefNumber >>> clicked');
      const valueValid = refNumberPhoneValid(value);

      if (valueValid) {
        let bodyPayload = getBodyPayloadFiltered(applyFiltered);

        bodyPayload = {
          ...bodyPayload,
          orderRef: valueValid,
        };

        setTransactions(null);
        fetchTransactionsFiltered({
          token,
          body: bodyPayload,
          page: PAGE_DEFAULT,
          setTransactions,
          setCurrentPage,
          setTotalPages,
          setTotalElements,
          setFetching,
        });
      } else {
        setTransactions(null);
        setFetching(false);
      }
    }
  };

  const handleSearchTransaction = () => {
    // An empty submit while a clear-reload is pending runs that reload now.
    if (!searchValue && clearReloadPendingRef.current) {
      reloadUnsearchedList();
      return;
    }

    if (!searchTextValid(searchValue)) {
      lastSubmittedRef.current = null;
      setSearchSubmittedInvalid(true);
      // Show the no-result state without a request. A dropped in-flight fetch
      // skips its setFetching(false), so clear it here. [] (not null) keeps
      // the page's initial-load effect off.
      invalidatePendingFxTransactionsFetches();
      setTransactions([]);
      setFetching(false);
      setSortOrder(FX_TRANSACTION_TABLE_SORT_ORDER_INITIAL);
      return;
    }

    lastSubmittedRef.current = normalizeSearchText(searchValue);
    setSearchSubmittedInvalid(false);

    setFetching(true);
    searchByRefNumber(searchValue);
    setSortOrder(FX_TRANSACTION_TABLE_SORT_ORDER_INITIAL);
  };

  const handleOnChange = (e) => {
    const { value } = e.target;
    setSearchSubmittedInvalid(false);

    if (value?.length <= REF_NUMBER_LENGTH_MAX) {
      setSearchValue(value);
      if (!value) {
        clearReloadPendingRef.current = true;
        if (isEmpty(transactions)) {
          // A response already in flight can clear isFetching and, if it leaves
          // the list empty, hide this input (desktop, no filters, empty text),
          // cancelling its queued reload; drop it.
          invalidatePendingFxTransactionsFetches();
          clearHeldSpinnerRef.current = true;
          setFetching(true);
        }
        debounce();
      } else {
        releaseClearSpinner();
      }
    }
  };

  const handleOnKeyDown = (e) => {
    const { key } = e;
    if (key === ENTER_KEY_DOWN) {
      handleSearchTransaction();
    }
  };

  // Leaving the field submits like Enter. Blank text, a window or tab switch
  // and a repeat of the last submitted text are left alone.
  const handleOnBlur = () => {
    if (!document.hasFocus()) return;
    if (!searchValue?.trim()) return;
    if (normalizeSearchText(searchValue) === lastSubmittedRef.current) return;

    handleSearchTransaction();
  };

  return (
    <SearchTransactionStyled>
      <SearchInput
        name={TRANSACTION_SEARCH_INPUT_FIELD}
        type="text"
        value={searchValue || ''}
        placeholder={t('fx_search_reference')}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onBlur={handleOnBlur}
      />
      <SearchIconStyled
        src={SearchIcon}
        width={24}
        height={24}
        alt=""
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleSearchTransaction}
      />
      {isSearchInvalid && (
        <SearchErrorStyled role="alert">
          {t('form_msg_search_validation')}
        </SearchErrorStyled>
      )}
    </SearchTransactionStyled>
  );
};

const SearchTransactionStyled = styled.div`
  position: relative;
  height: 44px;
  width: 100%;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    height: unset;
    width: 287px;
  }
`;

const SearchInput = styled.input`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;

  color: var(--ds-c-grey-dark);
  border: 1px solid #b0b6bd;
  border-radius: 8px;
  background: var(--ds-c-white);
  width: 100%;
  height: 44px;
  padding: 10px;
  padding-right: 32px;
  float: left;

  &:focus,
  &:focus-visible {
    border: 1px solid #b0b6bd !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
    width: 287px;
  }
`;
const SearchIconStyled = styled.img`
  position: absolute;
  top: 10px;
  left: calc(100% - 32px);
  cursor: pointer;
  user-select: none;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    left: 254px;
  }
`;

const SearchErrorStyled = styled.p`
  position: absolute;
  top: 48px;
  left: 0;
  width: calc(100% - 32px);
  margin: 0;
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: var(--ds-c-red);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 100%;
    font-size: 14px;
    line-height: 17px;
  }
`;

export default SearchTransaction;
